import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Stack,
  Text,
  Title,
  Group,
  ActionIcon,
  Badge,
  Card,
  Loader,
  Box,
  Button,
  Progress,
  Transition,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconThumbUp,
  IconThumbDown,
  IconClock,
  IconSwipe,
  IconCheck,
  IconChartBar,
  IconLeaf,
} from '@tabler/icons-react';
import {
  motion,
  useMotionValue,
  useTransform,
  animate,
  type PanInfo,
} from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { RecipeImage } from '../UI/RecipeImage';
import { formatTime } from '../../utils/formatTime';
import { getMeatlessLabel } from '../../utils/recipeBadges';
import { CuisineBadge } from '../UI/CuisineBadge';

type TasteTunerRecipe = {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  cuisine: string;
  tags?: string[];
  totalTimeMinutes: number;
  difficulty: 'easy' | 'medium' | 'hard';
  // Drives the meatless badge, the same signal the wizard picker's cards show.
  dietaryRestrictions?: string[];
  // Per-serving nutrition, when the catalog recipe has it. Powers the optional
  // nutrition overlay (fields mirror RecipeNutrition.perServing in mealPlan.ts).
  nutrition?: {
    perServing: {
      calories: number;
      protein: number;
      carbohydrates: number;
      fat: number;
      fiber: number;
      sodium: number;
    };
  };
};

type TasteTunerProps = {
  onComplete?: () => void;
  onDismiss?: () => void;
  onViewPlan?: () => void;
  planReady?: boolean;
};

const SWIPE_THRESHOLD = 100;
const SWIPE_VELOCITY = 500;
const EXIT_X = 600;
const CARD_MAX_WIDTH = 340;
const CARD_HEIGHT = 440;
/**
 * Photo height, fixed rather than "whatever the text leaves over".
 *
 * The body below it varies: descriptions run one to four lines, and the meta
 * row wraps to two lines when a long cuisine, a long time and "Vegetarian" land
 * together. Left to the flexbox, the photo absorbed all of that — the tallest
 * bodies squeezed it to 218px while the rest sat at 250px, so the image jumped
 * size as you swiped.
 *
 * So budget for the worst case on every card instead: CARD_HEIGHT minus the
 * tallest body this card can produce (two-line title, four-line description,
 * wrapped meta row), measured in a browser at 222px. Cards with less to say pad
 * the difference below the text, where nothing is moving.
 */
const CARD_IMAGE_HEIGHT = CARD_HEIGHT - 222;

type SwipeAPI = {
  swipe: (direction: 'left' | 'right') => void;
};

const SwipeCard = ({
  recipe,
  meatlessLabel,
  isTop,
  isLast,
  isTouch,
  onSwipe,
  apiRef,
}: {
  recipe: TasteTunerRecipe;
  // Null when there's nothing to say — not meatless, or the viewer's own diet
  // makes saying so redundant. Resolved by the parent, which holds the profile.
  meatlessLabel: string | null;
  isTop: boolean;
  isLast: boolean;
  isTouch: boolean;
  onSwipe: (direction: 'left' | 'right') => void;
  apiRef?: React.MutableRefObject<SwipeAPI | null>;
}) => {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 0, 200], [-12, 0, 12]);
  const yumOpacity = useTransform(x, [0, SWIPE_THRESHOLD], [0, 1]);
  const passOpacity = useTransform(x, [-SWIPE_THRESHOLD, 0], [1, 0]);

  // Optional nutrition scrim: the chart button toggles it on every platform (no
  // drawer — mirrors RecipeCard demo mode). Gated on nutrition existing, NOT on
  // isTop: the card behind the top one is revealed
  // mid-swipe (before currentIndex advances and isTop flips), so gating on isTop
  // would make the button pop in a beat after the swipe. It's covered until
  // revealed, and showNutrition only toggles via interaction, so rendering it on
  // the not-yet-top card is harmless.
  const [showNutrition, setShowNutrition] = useState(false);
  const perServing = recipe.nutrition?.perServing;
  const hasNutrition = !!perServing;

  // Expose programmatic swipe to parent via ref
  useEffect(() => {
    if (!apiRef || !isTop) return;
    apiRef.current = {
      swipe: (direction: 'left' | 'right') => {
        const target = direction === 'right' ? EXIT_X : -EXIT_X;
        animate(x, target, { type: 'spring', duration: 0.5, bounce: 0 });
        onSwipe(direction);
      },
    };
    return () => {
      apiRef.current = null;
    };
  }, [apiRef, isTop, x, onSwipe]);

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    const xOffset = info.offset.x;
    const xVelocity = info.velocity.x;

    if (xOffset > SWIPE_THRESHOLD || xVelocity > SWIPE_VELOCITY) {
      animate(x, EXIT_X, { type: 'spring', duration: 0.4, bounce: 0 });
      onSwipe('right');
    } else if (xOffset < -SWIPE_THRESHOLD || xVelocity < -SWIPE_VELOCITY) {
      animate(x, -EXIT_X, { type: 'spring', duration: 0.4, bounce: 0 });
      onSwipe('left');
    } else {
      animate(x, 0, { type: 'spring', stiffness: 500, damping: 30 });
    }
  };

  return (
    <motion.div
      style={{
        position: 'absolute',
        width: '100%',
        height: CARD_HEIGHT,
        x,
        rotate,
        cursor: isTop && isTouch ? 'grab' : 'default',
        zIndex: isTop ? 10 : 1,
        touchAction: 'none',
        borderRadius: 'var(--mantine-radius-lg)',
        boxShadow: isLast && isTop ? '0 2px 4px rgba(0,0,0,0.08), 0 8px 20px rgba(0,0,0,0.12)' : undefined,
      }}
      drag={isTop && isTouch ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.9}
      onDragEnd={isTop ? handleDragEnd : undefined}
      whileDrag={{
        cursor: 'grabbing',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05), 0 6px 14px rgba(0,0,0,0.08)',
      }}
    >
      <Card
        radius="lg"
        p={0}
        style={{
          width: '100%',
          height: CARD_HEIGHT,
          overflow: 'hidden',
          userSelect: 'none',
        }}
      >
        {/* flexShrink 0 is what actually pins it: the Card is a flex column and
            this Box would otherwise be the only thing able to give, since the
            body Stack is flex: 1 and so has a basis of 0. */}
        <Box
          pos="relative"
          style={{
            height: CARD_IMAGE_HEIGHT,
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          <RecipeImage
            src={recipe.imageUrl}
            alt={recipe.name}
            h={CARD_IMAGE_HEIGHT}
            w="100%"
            fit="cover"
            draggable={false}
          />

          {/* Nutrition scrim — same treatment as RecipeCard / Recipe Details.
              Toggled by the chart button below (all platforms; no hover so it's
              discoverable). Tapping the revealed scrim dismisses it. */}
          {hasNutrition && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                setShowNutrition(false);
              }}
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background:
                  'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 45%, rgba(0,0,0,0.3) 75%, transparent 100%)',
                padding: '40px 14px 12px',
                opacity: showNutrition ? 1 : 0,
                transition: 'opacity 0.2s ease',
                pointerEvents: showNutrition ? 'auto' : 'none',
                zIndex: 5,
              }}
            >
              <Text size="xs" fw={600} c="white" mb={4}>
                Per serving
              </Text>
              <Text size="sm" c="white">
                {Math.round(perServing!.calories)} cal ·{' '}
                {Math.round(perServing!.fat)}g fat ·{' '}
                {Math.round(perServing!.sodium)}mg sodium
              </Text>
              <Text size="sm" c="white">
                {Math.round(perServing!.carbohydrates)}g carbs ·{' '}
                {Math.round(perServing!.fiber)}g fiber ·{' '}
                {Math.round(perServing!.protein)}g protein
              </Text>
            </Box>
          )}

          {/* Chart button toggles the scrim on every platform (a visible
              affordance beats hover). Stop the pointer/click from reaching the
              card's drag handler. */}
          {hasNutrition && (
            <ActionIcon
              size="lg"
              radius="xl"
              variant="transparent"
              c="white"
              aria-label="Nutrition facts"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setShowNutrition((prev) => !prev);
              }}
              style={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                zIndex: 6,
                backgroundColor: 'rgba(0,0,0,0.45)',
              }}
            >
              <IconChartBar size={19} stroke={1.8} />
            </ActionIcon>
          )}
        </Box>

        {/* YUM overlay — opacity tracks drag position */}
        {isTop && (
          <motion.div
            style={{
              position: 'absolute',
              top: 20,
              left: 20,
              background: 'rgba(34, 139, 34, 0.85)',
              borderRadius: 'var(--mantine-radius-md)',
              padding: '8px 20px',
              transform: 'rotate(-15deg)',
              zIndex: 10,
              opacity: yumOpacity,
              pointerEvents: 'none',
            }}
          >
            <Text fw={700} size="xl" c="white">
              YUM
            </Text>
          </motion.div>
        )}

        {/* PASS overlay — opacity tracks drag position */}
        {isTop && (
          <motion.div
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              background: 'rgba(200, 60, 60, 0.85)',
              borderRadius: 'var(--mantine-radius-md)',
              padding: '8px 20px',
              transform: 'rotate(15deg)',
              zIndex: 10,
              opacity: passOpacity,
              pointerEvents: 'none',
            }}
          >
            <Text fw={700} size="xl" c="white">
              PASS
            </Text>
          </motion.div>
        )}

        <Stack gap="xs" p="md" style={{ flex: 1 }}>
          <Text size="lg" fw={700} lineClamp={2} lh={1.3}>
            {recipe.name}
          </Text>
          <Text size="sm" c="dimmed" lineClamp={4}>
            {recipe.description}
          </Text>
          {/* Four items now. Measured at 340px: this fits on one line for most
              recipes (Vietnamese + Vegan leaves 21px spare, Indian + Vegan 67px)
              and wraps only when a long cuisine, a long time and "Vegetarian"
              land together — Mediterranean + 1 hr 35 min + Vegetarian runs 41px
              over, past what tightening the gap or dropping the leaf could save.
              On those cards the photo absorbs the extra row and renders 218px
              instead of 250px: this Stack is flex: 1, so its basis is 0 and it
              takes none of the shrink. Nothing clips, and the tuner shows one
              card at a time so there's nothing to compare the shorter photo
              against. */}
          <Group gap="sm" mt="auto">
            <CuisineBadge cuisine={recipe.cuisine} />
            <Group gap={4} wrap="nowrap">
              <IconClock size={14} color="var(--mantine-color-dimmed)" />
              <Text size="xs" c="dimmed">
                {formatTime(recipe.totalTimeMinutes)}
              </Text>
            </Group>
            <Badge size="xs" variant="light" color={recipe.difficulty === 'easy' ? 'green' : recipe.difficulty === 'medium' ? 'yellow' : 'red'}>
              {recipe.difficulty}
            </Badge>
            {/* Same signal, wording and leaf as the wizard picker's cards, so a
                recipe reads the same whichever surface it turns up on. In the
                body rather than over the photo: the image already carries the
                nutrition button, and YUM/PASS sweep across its top corners on
                every drag. */}
            {meatlessLabel && (
              <Badge
                size="xs"
                variant="light"
                color="green"
                leftSection={<IconLeaf size={11} />}
                style={{ textTransform: 'none' }}
              >
                {meatlessLabel}
              </Badge>
            )}
          </Group>
        </Stack>
      </Card>
    </motion.div>
  );
};

export const TasteTuner = ({ onComplete, onDismiss, onViewPlan, planReady }: TasteTunerProps) => {
  const { user } = useAuth();
  const isTouch = useMediaQuery('(pointer: coarse)');
  const isTallEnough = useMediaQuery('(min-height: 716px)');
  const [recipes, setRecipes] = useState<TasteTunerRecipe[]>([]);
  // Held so the meatless badge can be suppressed for viewers already on a
  // meatless diet, for whom it would be noise on every card
  const [userDiets, setUserDiets] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [swiping, setSwiping] = useState(false);
  const [swipedCount, setSwipedCount] = useState(0);
  const topCardRef = useRef<SwipeAPI | null>(null);

  // Lock body scroll while overlay is mounted
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (!user) return;

    const fetchRecipes = async () => {
      try {
        const token = await user.getIdToken();
        const profileResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/user/profile`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!profileResponse.ok) return;

        const profileData = await profileResponse.json();
        const familyProfile = profileData.user?.familyProfile;
        if (!familyProfile) return;
        setUserDiets(familyProfile.dietaryRestrictions ?? []);

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/user/taste-tuner-recipes`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ familyProfile }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          const fetchedRecipes = data.recipes || [];
          setRecipes(fetchedRecipes);
          setCurrentIndex(fetchedRecipes.length - 1);
        }
      } catch (error) {
        console.error('Taste tuner fetch failed:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecipes();
  }, [user]);

  const handleSwipe = useCallback(async (direction: 'left' | 'right', recipe: TasteTunerRecipe) => {
    if (!user || swiping) return;
    setSwiping(true);
    setSwipedCount((prev) => prev + 1);

    const vote = direction === 'right' ? 'up' : 'down';

    // Let exit animation play before removing the card
    setTimeout(() => {
      setCurrentIndex((prev) => prev - 1);
      setSwiping(false);
    }, 350);

    try {
      const token = await user.getIdToken();
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/taste-preview-vote`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          // source separates tuner swipes from onboarding ones in the
          // event log; both carry the same weight.
          body: JSON.stringify({
            catalogRecipeId: recipe.id,
            vote,
            source: 'taste_tuner',
          }),
        }
      );
    } catch (error) {
      console.error('Taste tuner vote failed:', error);
    }
  }, [user, swiping]);

  const triggerSwipe = useCallback((direction: 'left' | 'right') => {
    topCardRef.current?.swipe(direction);
  }, []);

  // When all cards are swiped
  useEffect(() => {
    if (recipes.length > 0 && currentIndex < 0) {
      onComplete?.();
    }
  }, [currentIndex, recipes.length, onComplete]);

  const progress = recipes.length > 0 ? (swipedCount / recipes.length) * 100 : 0;
  const cardsRemaining = currentIndex >= 0;

  if (loading) {
    return (
      <Box
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 200,
          background: '#fffaf5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Stack align="center" gap="md">
          <Loader color="orange" size="lg" />
          <Text c="dimmed" size="sm">Finding recipes for you...</Text>
        </Stack>
      </Box>
    );
  }

  if (recipes.length === 0) {
    onComplete?.();
    return null;
  }

  return (
    <Box
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: '#fffaf5',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Stack
        gap={4}
        align="center"
        pt="xl"
        pb="sm"
        px="md"
        style={{ width: '100%', maxWidth: 400 }}
      >
        <Title order={3} ta="center">
          While we build your plan...
        </Title>
        <Text c="dimmed" size="sm" ta="center">
          {isTouch
            ? 'Swipe right on recipes you like, left to pass'
            : 'Rate a few recipes so we can learn your taste'}
        </Text>
        <Progress
          value={progress}
          size="xs"
          color="orange"
          radius="xl"
          w="100%"
          mt="xs"
        />
      </Stack>

      {/* Plan ready toast — floats above card stack */}
      <Transition mounted={!!planReady && cardsRemaining && !!isTallEnough} transition="slide-down" duration={300}>
        {(styles) => (
          <Button
            variant="light"
            color="green"
            radius="xl"
            size="sm"
            leftSection={<IconCheck size={16} />}
            style={{ ...styles, marginTop: 8, marginBottom: -4 }}
            onClick={onViewPlan || onComplete}
          >
            Your plan is ready — view it now
          </Button>
        )}
      </Transition>

      {/* Card stack + controls */}
      <Box
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          padding: '0 16px',
          gap: 36,
        }}
      >
        <Box
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: CARD_MAX_WIDTH,
            height: CARD_HEIGHT,
            borderRadius: 'var(--mantine-radius-lg)',
            boxShadow: currentIndex > 0 ? '0 2px 4px rgba(0,0,0,0.08), 0 8px 20px rgba(0,0,0,0.12)' : 'none',
          }}
        >
          {recipes.map((recipe, index) => {
            if (index > currentIndex || index < currentIndex - 1) return null;
            const isTop = index === currentIndex;
            return (
              <SwipeCard
                key={recipe.id}
                recipe={recipe}
                meatlessLabel={getMeatlessLabel(recipe, {
                  dietaryRestrictions: userDiets,
                })}
                isTop={isTop}
                isLast={currentIndex === 0}
                isTouch={!!isTouch}
                onSwipe={(dir) => handleSwipe(dir, recipe)}
                apiRef={isTop ? topCardRef : undefined}
              />
            );
          })}
        </Box>

        <Stack gap="md" align="center">
        {isTouch ? (
          <Group gap="xs" c="dimmed">
            <IconSwipe size={16} />
            <Text size="xs">Swipe to rate</Text>
          </Group>
        ) : (
          <Group gap="lg">
            <ActionIcon
              size={48}
              radius="xl"
              variant="light"
              color="red"
              onClick={() => triggerSwipe('left')}
              disabled={!cardsRemaining || swiping}
            >
              <IconThumbDown size={24} />
            </ActionIcon>
            <ActionIcon
              size={48}
              radius="xl"
              variant="light"
              color="green"
              onClick={() => triggerSwipe('right')}
              disabled={!cardsRemaining || swiping}
            >
              <IconThumbUp size={24} />
            </ActionIcon>
          </Group>
        )}

        {onDismiss && (
          <Button
            variant="subtle"
            color="dimmed"
            size="xs"
            onClick={onDismiss}
          >
            Don't show again
          </Button>
        )}
      </Stack>
      </Box>
    </Box>
  );
};
